import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { LoadingState } from "../components/common/LoadingState";
import { ErrorState } from "../components/common/ErrorState";
import { PageHeader } from "../components/common/PageHeader";
import { reportAPI } from "../api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { ShieldBan, UserX, ChevronLeft, ChevronRight, Search, Clock, History } from "lucide-react";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { ScrollArea } from "../components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import type { BannedUser, UnbanUserRequest, BanHistory } from "../types/report";

export function BannedUsersPage() {
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [unbanDialogOpen, setUnbanDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<BannedUser | null>(null);
  const [unbanReason, setUnbanReason] = useState("");
  const queryClient = useQueryClient();

  const pageLimit = 20;

  // Debounce search input and reset pagination
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Fetch banned users
  const { data: bannedUsersData, isLoading: usersLoading, error: usersError } = useQuery({
    queryKey: ['bannedUsers', searchTerm, currentPage],
    queryFn: () => reportAPI.getBannedUsers({
      search: searchTerm || undefined,
      page: currentPage,
      limit: pageLimit,
    }),
  });

  // Fetch user ban history
  const { data: banHistoryData, isLoading: historyLoading } = useQuery({
    queryKey: ['userBanHistory', selectedUser?._id],
    queryFn: () => reportAPI.getUserBanHistory(selectedUser!._id),
    enabled: !!selectedUser && historyDialogOpen,
  });

  // Unban user mutation
  const unbanUserMutation = useMutation({
    mutationFn: ({ userId, data }: { userId: string; data?: UnbanUserRequest }) =>
      reportAPI.unbanUser(userId, data),
    onSuccess: () => {
      toast.success("User unbanned successfully");
      queryClient.invalidateQueries({ queryKey: ['bannedUsers'] });
      handleCloseUnbanDialog();
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || "Failed to unban user");
    },
  });

  const bannedUsers = bannedUsersData?.users || [];
  const pagination = bannedUsersData?.pagination || { page: 1, limit: pageLimit, total: 0, pages: 0 };

  const handleUnbanUser = (user: BannedUser) => {
    setSelectedUser(user);
    setUnbanDialogOpen(true);
  };

  const handleViewHistory = (user: BannedUser) => {
    setSelectedUser(user);
    setHistoryDialogOpen(true);
  };

  const handleCloseUnbanDialog = () => {
    setUnbanDialogOpen(false);
    setSelectedUser(null);
    setUnbanReason("");
  };

  const handleCloseHistoryDialog = () => {
    setHistoryDialogOpen(false);
    setSelectedUser(null);
  };

  const handleConfirmUnban = () => {
    if (!selectedUser) return;
    const data: UnbanUserRequest | undefined = unbanReason ? { unban_reason: unbanReason } : undefined;
    unbanUserMutation.mutate({
      userId: selectedUser._id,
      data,
    });
  };

  const getUserInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const formatBanDuration = (user: BannedUser) => {
    if (!user.ban_expires_at) return 'Permanent';
    const expiresAt = new Date(user.ban_expires_at);
    const now = new Date();
    if (expiresAt < now) return 'Expired';
    const days = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return `${days} day${days !== 1 ? 's' : ''} remaining`;
  };

  if (usersLoading) {
    return (
      <DashboardLayout>
        <LoadingState message="Loading banned users..." />
      </DashboardLayout>
    );
  }

  if (usersError) {
    return (
      <DashboardLayout>
        <ErrorState message="Failed to load banned users" />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <PageHeader
          title="Banned Users"
          description="Manage banned users across all campuses and universities"
        />

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Banned</CardTitle>
              <UserX className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{pagination.total}</div>
              <p className="text-xs text-muted-foreground">Across all campuses</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Permanent Bans</CardTitle>
              <ShieldBan className="h-4 w-4 text-destructive" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {bannedUsers.filter(u => !u.ban_expires_at).length}
              </div>
              <p className="text-xs text-muted-foreground">No expiration</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Temporary Bans</CardTitle>
              <Clock className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {bannedUsers.filter(u => u.ban_expires_at).length}
              </div>
              <p className="text-xs text-muted-foreground">With expiration</p>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              Search Users
            </CardTitle>
            <CardDescription>Search by name or email</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input
                placeholder="Search banned users..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="max-w-md"
              />
              <Button
                variant="outline"
                onClick={() => {
                  setSearchInput("");
                }}
              >
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Banned Users Table */}
        <Card>
          <CardHeader>
            <CardTitle>Banned Users</CardTitle>
            <CardDescription>
              Showing {bannedUsers.length} of {pagination.total} banned users
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>School</TableHead>
                    <TableHead>Campus</TableHead>
                    <TableHead>Ban Date</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Reports</TableHead>
                    <TableHead>Banned By</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bannedUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                        No banned users found
                      </TableCell>
                    </TableRow>
                  ) : (
                    bannedUsers.map((user) => (
                      <TableRow key={user._id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar>
                              <AvatarImage src={user.avatar_url} />
                              <AvatarFallback>
                                {getUserInitials(user.first_name, user.last_name)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <div className="font-medium">
                                {user.first_name} {user.last_name}
                              </div>
                              <div className="text-xs text-muted-foreground">{user.email}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{user.school_id?.name || '-'}</TableCell>
                        <TableCell>{user.campus_id?.campus_name || '-'}</TableCell>
                        <TableCell>
                          {new Date(user.ban_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Badge variant={user.ban_expires_at ? "outline" : "destructive"}>
                            {formatBanDuration(user)}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-xs truncate">
                          {user.ban_reason || '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {user.report_count || 0}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {user.banned_by ? (
                            <div className="text-sm">
                              {user.banned_by.first_name} {user.banned_by.last_name}
                            </div>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewHistory(user)}
                            >
                              <History className="h-4 w-4 mr-1" />
                              History
                            </Button>
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => handleUnbanUser(user)}
                            >
                              Unban
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-muted-foreground">
                  Page {pagination.page} of {pagination.pages}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(pagination.pages, p + 1))}
                    disabled={currentPage === pagination.pages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Unban User Dialog */}
        <Dialog open={unbanDialogOpen} onOpenChange={(open) => !open && handleCloseUnbanDialog()}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Unban User</DialogTitle>
              <DialogDescription>
                This will restore access for {selectedUser?.first_name} {selectedUser?.last_name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  <Avatar>
                    <AvatarImage src={selectedUser?.avatar_url} />
                    <AvatarFallback>
                      {selectedUser && getUserInitials(selectedUser.first_name, selectedUser.last_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="font-medium">
                      {selectedUser?.first_name} {selectedUser?.last_name}
                    </div>
                    <div className="text-sm text-muted-foreground">{selectedUser?.email}</div>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Unban Reason (Optional)</Label>
                <Textarea
                  value={unbanReason}
                  onChange={(e) => setUnbanReason(e.target.value)}
                  placeholder="Explain why this user is being unbanned..."
                  maxLength={500}
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">{unbanReason.length}/500 characters</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleCloseUnbanDialog}>
                Cancel
              </Button>
              <Button
                onClick={handleConfirmUnban}
                disabled={unbanUserMutation.isPending}
              >
                {unbanUserMutation.isPending ? "Unbanning..." : "Unban User"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Ban History Dialog */}
        <Dialog open={historyDialogOpen} onOpenChange={(open) => !open && handleCloseHistoryDialog()}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>Ban History</DialogTitle>
              <DialogDescription>
                Complete ban history for {selectedUser?.first_name} {selectedUser?.last_name}
              </DialogDescription>
            </DialogHeader>
            {historyLoading ? (
              <div className="py-8">
                <LoadingState message="Loading ban history..." />
              </div>
            ) : banHistoryData ? (
              <Tabs defaultValue="history" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="history">Ban History</TabsTrigger>
                  <TabsTrigger value="reports">Reports ({banHistoryData.reports.length})</TabsTrigger>
                </TabsList>

                <TabsContent value="history">
                  <ScrollArea className="h-[400px]">
                    <div className="space-y-4">
                      {banHistoryData.ban_history.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">
                          No ban history available
                        </p>
                      ) : (
                        banHistoryData.ban_history.map((ban: BanHistory, index: number) => (
                          <Card key={index}>
                            <CardContent className="pt-6">
                              <div className="space-y-3">
                                <div className="flex items-start justify-between">
                                  <div>
                                    <div className="font-medium">
                                      Banned on {new Date(ban.banned_at).toLocaleString()}
                                    </div>
                                    <div className="text-sm text-muted-foreground">
                                      By {ban.banned_by.first_name} {ban.banned_by.last_name}
                                    </div>
                                  </div>
                                  <Badge variant={ban.unbanned_at ? "secondary" : "destructive"}>
                                    {ban.unbanned_at ? "Unbanned" : "Active"}
                                  </Badge>
                                </div>

                                <div>
                                  <Label className="text-xs text-muted-foreground">Reason</Label>
                                  <p className="text-sm mt-1">{ban.reason}</p>
                                </div>

                                {ban.duration && (
                                  <div>
                                    <Label className="text-xs text-muted-foreground">Duration</Label>
                                    <p className="text-sm mt-1">{ban.duration} days</p>
                                  </div>
                                )}

                                {ban.unbanned_at && (
                                  <div className="pt-3 border-t">
                                    <div className="text-sm">
                                      <span className="font-medium">Unbanned:</span>{' '}
                                      {new Date(ban.unbanned_at).toLocaleString()}
                                    </div>
                                    {ban.unbanned_by && (
                                      <div className="text-sm text-muted-foreground">
                                        By {ban.unbanned_by.first_name} {ban.unbanned_by.last_name}
                                      </div>
                                    )}
                                    {ban.unban_reason && (
                                      <div className="text-sm mt-2">
                                        <Label className="text-xs text-muted-foreground">Unban Reason</Label>
                                        <p className="mt-1">{ban.unban_reason}</p>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>

                <TabsContent value="reports">
                  <ScrollArea className="h-[400px]">
                    <div className="space-y-4">
                      {banHistoryData.reports.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">
                          No reports found
                        </p>
                      ) : (
                        banHistoryData.reports.map((report) => (
                          <Card key={report._id}>
                            <CardContent className="pt-6">
                              <div className="space-y-2">
                                <div className="flex items-start justify-between">
                                  <div>
                                    <Badge variant="outline">{report.report_type}</Badge>
                                    <Badge variant="secondary" className="ml-2">
                                      {report.status}
                                    </Badge>
                                  </div>
                                  <div className="text-sm text-muted-foreground">
                                    {new Date(report.createdAt).toLocaleDateString()}
                                  </div>
                                </div>
                                <div className="text-sm">
                                  <span className="font-medium">Reason:</span> {report.reason}
                                </div>
                                <div className="text-sm">{report.description}</div>
                              </div>
                            </CardContent>
                          </Card>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>
              </Tabs>
            ) : null}
            <DialogFooter>
              <Button variant="outline" onClick={() => setHistoryDialogOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
